import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/jwt-auth-client";

export interface ArtifactRecord {
  id: string;
  session: string | null;
  task: string | null;
  workspace: string | null;
  coagent_type: string | null;
  name: string;
  subject: string | null;
  brand_id: string | null;
  content_md: string;
  storage_key: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  session_name: string | null;
  workspace_name: string | null;
  session_task_type: string | null;
  session_workflow_slug: string | null;
  is_logo: boolean;
}

export interface ArtifactFilters {
  workspace?: string;
  coagent_type?: string;
  session_type?: string;
  subject?: string;
  session_id?: string;
  /** BrandConfig.identity.id — filters to artifacts tagged with this brand
   *  (ads:research, social:research, ugc-portrait, …). Used by the brand
   *  detail page's research history. */
  brand_id?: string;
  /** UI-only filter (consumed by ArtifactList's type dropdown). The fetch
   *  function ignores it today — kept here so callers can hold the state in
   *  the same object without a TS error. */
  type?: string;
}

// Stable empty-array reference. Returning `query.data ?? []` would mint a
// fresh array on every render and re-trigger consumer useEffects/useMemos.
const EMPTY_ARTIFACTS: ArtifactRecord[] = [];

/** Query keys for the artifacts API. Scoped under `["artifacts"]`. */
export const artifactsKeys = {
  all: ["artifacts"] as const,
  list: (filters: ArtifactFilters | undefined) =>
    ["artifacts", "list", filters ?? {}] as const,
};

async function fetchArtifacts(
  filters: ArtifactFilters | undefined,
  signal?: AbortSignal,
): Promise<ArtifactRecord[]> {
  const params = new URLSearchParams();
  if (filters?.workspace) params.set("workspace", filters.workspace);
  if (filters?.coagent_type) params.set("coagent_type", filters.coagent_type);
  if (filters?.session_type) params.set("session_type", filters.session_type);
  if (filters?.subject) params.set("subject", filters.subject);
  if (filters?.session_id) params.set("session_id", filters.session_id);
  if (filters?.brand_id) params.set("brand_id", filters.brand_id);
  const url = `/api/artifacts/${params.toString() ? `?${params}` : ""}`;
  const res = await apiFetch(url, { signal });
  if (!res.ok) throw new Error("Failed to fetch artifacts");
  const data = (await res.json()) as unknown;
  return Array.isArray(data) ? (data as ArtifactRecord[]) : [];
}

export interface UseArtifactsOptions {
  /** Background refetch cadence in ms. Pass `false` (or omit) to disable.
   *  Used by live moodboard / asset surfaces to surface backend-persisted
   *  artifacts while in-flight jobs (Banan, Kling) are still running. */
  refetchInterval?: number | false;
}

/**
 * useArtifacts — React Query wrapper for the artifacts list.
 *
 * Public signature unchanged from the pre-Phase-6 version: returns
 * `{ artifacts, loading, error, refresh }`. The query key includes
 * the filter object so distinct filter combos cache separately.
 */
export function useArtifacts(
  filters?: ArtifactFilters,
  options?: UseArtifactsOptions,
) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: artifactsKeys.list(filters),
    queryFn: ({ signal }) => fetchArtifacts(filters, signal),
    refetchInterval: options?.refetchInterval ?? false,
  });

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: artifactsKeys.list(filters) });
  }, [qc, filters]);

  return {
    artifacts: query.data ?? EMPTY_ARTIFACTS,
    loading: query.isLoading,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : String(query.error)
      : null,
    refresh,
  };
}
